import { useState, useEffect, useRef, useCallback } from 'react';

export const useAnimate = ({
	in: defaultState = false,
	duration = 300,
	easing = 'ease',
	animation,
}) => {
	const [state, setState] = useState(defaultState);
	const [show, setShow] = useState(defaultState);
	const [mounted, setMounted] = useState(false);
	const ref = useRef(null);
	const toggle = useCallback(() => {
		setShow((prev) => !prev);
	}, []);

	useEffect(() => {
		const target = ref.current;
		if (!target) return;
		let cleanup;
		let { enter = {}, exit = {} } = animation;
		if (!mounted) {
			const defaultcss = {
				...(state ? enter : exit),
			};
			if (hasAuto(enter)) {
				defaultcss.overflow = 'hidden';
			}
			css(target, defaultcss);
			setMounted(true);
		}
		if (show && mounted) {
			const auto = hasAuto(enter);
			if (auto) enter = { ...enter, [auto]: `${target.scrollHeight}px` };

			setState(true);
			css(target, { ...enter, transition: `all ${duration}ms ${easing}` });
		}

		if (!show && state) {
			css(target, { ...exit, transition: `all ${duration}ms ${easing}` });
			cleanup = setTimeout(() => {
				setState(false);
			}, duration);
		}

		return () => cleanup && clearTimeout(cleanup);
	}, [show, state, mounted, duration, easing, animation, ref]);

	return [ref, state || show, toggle];
};

export const useAnimateGroup = (props, accordion = true) => {
	const [state, setState] = useState({});
	const useRegister = (id, defaults) => {
		const [ref, show, toggle] = useAnimate({ ...props, in: defaults });

		useEffect(() => {
			setState((prev) => ({ ...prev, [id]: { show, toggle } }));
			return () =>
				setState((prev) => {
					delete prev[id];
					return { ...prev };
				});
		}, [id, show, toggle]);

		return ref;
	};
	const show = (id) => state[id] && state[id].show;
	const toggle = (id) => {
		if (accordion && state[id].show) return;
		const active = Object.keys(state).find(
			(key) => state[key].show && key !== id
		);
		accordion && active && state[active].toggle();
		state[id].toggle();
	};

	return [useRegister, show, toggle];
};

const hasAuto = (style) =>
	Object.keys(style).find(
		(name) => /([hH]eight|[wW]idth)/.test(name) && style[name] === 'auto'
	);

const css = (el, style) =>
	Object.keys(style).forEach((prop) => (el.style[prop] = style[prop]));
